/**
 * Thin fetch wrapper that:
 *   - sends cookies on every request (`credentials: 'include'`)
 *   - generates an Idempotency-Key for mutating verbs
 *   - throws Problem+JSON-shaped errors so call sites can switch on .code
 */
import { ulid } from 'ulid'

export interface ApiError extends Error {
  code: string
  status: number
  requestId: string
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers)
  if (!headers.has('content-type') && init.body) {
    headers.set('content-type', 'application/json')
  }
  const method = (init.method ?? 'GET').toUpperCase()
  if (method !== 'GET' && method !== 'HEAD' && !headers.has('idempotency-key')) {
    headers.set('idempotency-key', ulid())
  }

  const res = await fetch(path, { ...init, credentials: 'include', headers })
  const text = await res.text()
  let parsed: unknown
  try { parsed = text ? JSON.parse(text) : null } catch { parsed = null }

  if (!res.ok) {
    const body = (parsed ?? {}) as Record<string, unknown>
    const err = new Error(String(body['title'] ?? `HTTP ${res.status}`)) as ApiError
    err.code = String(body['type'] ?? 'INTERNAL').replace(/^.*\//, '')
    err.status = res.status
    err.requestId = String(body['requestId'] ?? '')
    throw err
  }
  return parsed as T
}

export const get = <T>(path: string) => api<T>(path)
export const post = <T>(path: string, body?: unknown) => {
  const init: RequestInit = { method: 'POST' }
  if (body !== undefined) init.body = JSON.stringify(body)
  return api<T>(path, init)
}
export const postWithAuth = <T>(
  path: string,
  body: unknown,
  extraHeaders: Record<string, string> = {},
) => {
  const init: RequestInit = {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
    headers: extraHeaders,
  }
  return api<T>(path, init)
}
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' })
